// src/pages/AppDownload.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Smartphone, Star, Shield, Truck, Zap, Heart, CheckCircle,
  ArrowRight, Download, Leaf, Bell, Gift, Lock, RefreshCw,
  TrendingUp, Users, Award, ArrowLeft
} from 'lucide-react';

// Real Google Play Icon
const PlayStoreIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
    <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" fill="#fff"/>
  </svg>
);

// Real Apple Icon
const AppleIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 384 512" xmlns="http://www.w3.org/2000/svg">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" fill="#fff"/>
  </svg>
);

const AppDownload = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const docRef = doc(db, 'settings', 'app-download');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setConfig(docSnap.data());
        }
      } catch (error) {
        console.error('Error fetching app config:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
    window.scrollTo(0, 0);
  }, []);

  // Auto redirect if hidden or inactive
  useEffect(() => {
    if (!loading && (!config || config.active === false)) {
      navigate('/', { replace: true });
    }
  }, [loading, config, navigate]);

  if (loading || !config || config.active === false) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-body-bg)' }}>
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent"
          style={{ borderColor: 'var(--color-link)', borderTopColor: 'transparent' }}></div>
      </div>
    );
  }

  const features = config.features || [];
  const playActive = config.playStoreUrl && config.playStoreActive !== false;
  const appActive = config.appStoreUrl && config.appStoreActive !== false;

  // ✅ Pink Theme Colors
  const bgFrom = config.bgGradientFrom || 'var(--color-button-bg)';
  const bgTo = config.bgGradientTo || 'var(--color-button-hover-bg)';
  const titleColor = config.titleColor || '#FFFFFF';
  const descColor = config.descriptionColor || '#FFFFFF';
  const badgeBg = config.badgeBg || 'rgba(255,255,255,0.25)';
  const badgeText = config.badgeText || '#FFFFFF';
  const learnMoreColor = config.learnMoreColor || '#FFFFFF';

  const renderIcon = (iconName, size = 26) => {
    const iconProps = { size, style: { color: 'var(--color-link)' } };
    switch (iconName) {
      case 'zap': return <Zap {...iconProps} />;
      case 'gift': return <Gift {...iconProps} />;
      case 'truck': return <Truck {...iconProps} />;
      case 'shield': return <Shield {...iconProps} />;
      case 'heart': return <Heart {...iconProps} />;
      case 'bell': return <Bell {...iconProps} />;
      case 'lock': return <Lock {...iconProps} />;
      case 'refresh': return <RefreshCw {...iconProps} />;
      case 'trending': return <TrendingUp {...iconProps} />;
      case 'users': return <Users {...iconProps} />;
      case 'award': return <Award {...iconProps} />;
      case 'download': return <Download {...iconProps} />;
      default: return <CheckCircle {...iconProps} />;
    }
  };

  return (
    <div className="min-h-screen anim-fadeIn" style={{ backgroundColor: 'var(--color-body-bg)' }}>

      {/* Back button */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-6">
        <button onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-full font-semibold text-sm btn-2d shadow-sm"
          style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)`, color: 'var(--color-primary-text)' }}>
          <ArrowLeft size={16} /> Back
        </button>
      </div>

      {/* HERO - PINK GRADIENT */}
      <section className="relative overflow-hidden py-12 md:py-20"
        style={{ background: `linear-gradient(135deg, ${bgFrom} 0%, ${bgTo} 100%)` }}>
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-white opacity-10 -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-white opacity-10 translate-y-1/2 -translate-x-1/2"></div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">

            <div className="text-center md:text-left" style={{ color: '#FFFFFF' }}>
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-md mb-5 anim-fadeInUp"
                style={{ backgroundColor: badgeBg, color: badgeText }}
              >
                <Leaf size={16} />
                <span className="text-xs font-bold uppercase tracking-wider">NAVIROSE App</span>
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight anim-fadeInUp delay-100"
                style={{ color: titleColor }}>
                {config.heroTitle || 'Shop Organic Anytime, Anywhere'}
              </h1>

              <p className="text-base md:text-lg mb-6 leading-relaxed anim-fadeInUp delay-200"
                style={{ color: descColor, opacity: 0.95 }}>
                {config.heroDescription || 'Download our app and get exclusive discounts, faster checkout, and track your orders in real-time.'}
              </p>

              <div className="flex items-center gap-3 justify-center md:justify-start mb-6 anim-fadeInUp delay-300">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={18} className="fill-yellow-300 text-yellow-300" />
                  ))}
                </div>
                <span className="text-sm font-semibold" style={{ color: titleColor }}>
                  4.9 ★ (12,000+ reviews)
                </span>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start anim-fadeInUp delay-400">
                {playActive && (
                  <a href={config.playStoreUrl} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-3 px-5 py-3 rounded-2xl bg-black text-white hover:scale-105 transition-all shadow-2xl btn-2d border border-white/10">
                    <PlayStoreIcon size={28} />
                    <div className="text-left">
                      <p className="text-[10px] opacity-80 uppercase tracking-wider">Get it on</p>
                      <p className="text-base font-bold leading-tight">Google Play</p>
                    </div>
                  </a>
                )}

                {appActive && (
                  <a href={config.appStoreUrl} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-3 px-5 py-3 rounded-2xl bg-black text-white hover:scale-105 transition-all shadow-2xl btn-2d border border-white/10">
                    <AppleIcon size={28} />
                    <div className="text-left">
                      <p className="text-[10px] opacity-80 uppercase tracking-wider">Download on the</p>
                      <p className="text-base font-bold leading-tight">App Store</p>
                    </div>
                  </a>
                )}
              </div>

              {config.qrCode && (
                <div className="mt-6 flex items-center gap-4 justify-center md:justify-start anim-fadeInUp delay-500">
                  <div className="p-3 bg-white rounded-2xl shadow-xl">
                    <img src={config.qrCode} alt="QR" className="w-24 h-24 object-contain" />
                  </div>
                  <div className="text-sm" style={{ color: titleColor }}>
                    <p className="font-bold">Scan to Download</p>
                    <p className="opacity-80 text-xs">iOS & Android</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-center anim-fadeInRight">
              <div className="relative">
                <div className="absolute inset-0 bg-white blur-3xl opacity-30 rounded-full"></div>
                <div className="relative w-64 md:w-80 bg-gray-900 rounded-[2.5rem] p-3 shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-500">
                  <div className="bg-white rounded-[2rem] overflow-hidden">
                    <div className="bg-gray-900 h-6 flex items-center justify-center">
                      <div className="w-16 h-1.5 bg-gray-800 rounded-full"></div>
                    </div>
                    {config.mockupImage ? (
                      <img src={config.mockupImage} alt="App preview" className="w-full h-96 object-cover" />
                    ) : (
                      <div className="w-full h-96 flex flex-col items-center justify-center p-4"
                        style={{ background: `linear-gradient(135deg, #FFF2F6 0%, #FFFFFF 100%)` }}>
                        <Leaf size={48} className="mb-3" style={{ color: 'var(--color-link)' }} />
                        <p className="font-bold text-center" style={{ color: 'var(--color-primary-text)' }}>NAVIROSE App</p>
                        <p className="text-xs mt-1 text-center" style={{ color: 'var(--color-secondary-text)' }}>Fresh. Fast. Easy.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* FEATURES */}
      {features.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-20">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-4xl font-bold mb-3" style={{ color: 'var(--color-primary-text)' }}>
              Why Download Our App?
            </h2>
            <p className="text-sm md:text-base" style={{ color: 'var(--color-secondary-text)' }}>
              Exclusive features sirf app users ke liye
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <div key={i} className="rounded-2xl p-6 text-center card-lift anim-fadeInUp"
                style={{
                  backgroundColor: 'var(--color-card-bg)',
                  border: `1px solid var(--color-border)`,
                  animationDelay: `${i * 100}ms`,
                }}>
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: '#FFF2F6' }}>
                  {renderIcon(feature.icon)}
                </div>
                <h3 className="font-bold text-lg mb-2" style={{ color: 'var(--color-primary-text)' }}>
                  {feature.title}
                </h3>
                <p className="text-sm" style={{ color: 'var(--color-secondary-text)' }}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* SCREENSHOTS */}
      {config.screenshots && config.screenshots.length > 0 && (
        <section className="py-12 md:py-16" style={{ backgroundColor: '#FFF2F6' }}>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-4xl font-bold mb-3" style={{ color: 'var(--color-primary-text)' }}>
                App Screenshots
              </h2>
              <p className="text-sm md:text-base" style={{ color: 'var(--color-secondary-text)' }}>
                Ek jhalak hamari app ki
              </p>
            </div>

            <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory"
              style={{ scrollbarWidth: 'thin' }}>
              {config.screenshots.map((shot, i) => (
                <div key={i} className="shrink-0 w-56 md:w-64 snap-start anim-scaleIn"
                  style={{ animationDelay: `${i * 100}ms` }}>
                  <div className="bg-gray-900 rounded-[2rem] p-2 shadow-xl">
                    <img src={shot} alt={`Screenshot ${i + 1}`}
                      className="w-full rounded-[1.7rem] object-cover" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA - PINK GRADIENT */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-20">
        <div className="rounded-3xl p-8 md:p-12 text-center relative overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${bgFrom} 0%, ${bgTo} 100%)` }}>

          <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-white opacity-10 -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-white opacity-10 translate-y-1/2 -translate-x-1/2"></div>

          <div className="relative z-10" style={{ color: '#FFFFFF' }}>
            <Smartphone size={64} className="mx-auto mb-4 anim-float" />
            <h2 className="text-2xl md:text-4xl font-bold mb-3" style={{ color: titleColor }}>
              Ready to Get Started?
            </h2>
            <p className="text-base md:text-lg mb-6 max-w-2xl mx-auto" style={{ color: descColor, opacity: 0.95 }}>
              Download the app now and start shopping organic products at your fingertips!
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {playActive && (
                <a href={config.playStoreUrl} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-black text-white hover:scale-105 transition btn-2d shadow-xl border border-white/10">
                  <PlayStoreIcon size={24} />
                  <div className="text-left">
                    <p className="text-[10px] opacity-80 uppercase tracking-wider">Get it on</p>
                    <p className="text-sm font-bold leading-tight">Google Play</p>
                  </div>
                </a>
              )}
              {appActive && (
                <a href={config.appStoreUrl} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-black text-white hover:scale-105 transition btn-2d shadow-xl border border-white/10">
                  <AppleIcon size={24} />
                  <div className="text-left">
                    <p className="text-[10px] opacity-80 uppercase tracking-wider">Download on the</p>
                    <p className="text-sm font-bold leading-tight">App Store</p>
                  </div>
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="h-20 md:h-0"></div>
    </div>
  );
};

export default AppDownload;